import { useTranslation } from "react-i18next";

export default function PrivacyPolicy() {
  const { t } = useTranslation("legal");

  return (
    <main className="flex flex-col p-4 overflow-auto h-dvh">
      <header>
        <h1 className="mb-4 text-2xl font-bold">{t("privacyPolicy.title")}</h1>
        <p className="mb-2 text-lg">{t("privacyPolicy.effectiveDate")}</p>
      </header>

      <section aria-label="Summary" className="mb-6">
        <p className="mb-4 text-base">
          <strong>{t("privacyPolicy.summary.title")}</strong>{" "}
          {t("privacyPolicy.summary.description")}
        </p>
        <p>{t("privacyPolicy.welcome")}</p>
      </section>

      <section aria-label="FAQ" className="w-full mb-6">
        <h2 className="mb-4 text-xl font-semibold">
          {t("privacyPolicy.faq.title")}
        </h2>
        <dl>
          <dt className="mt-3 font-bold">
            {t("privacyPolicy.faq.q1.question")}
          </dt>
          <dd className="ml-4">{t("privacyPolicy.faq.q1.answer")}</dd>

          <dt className="mt-3 font-bold">
            {t("privacyPolicy.faq.q2.question")}
          </dt>
          <dd className="ml-4">{t("privacyPolicy.faq.q2.answer")}</dd>

          <dt className="mt-3 font-bold">
            {t("privacyPolicy.faq.q3.question")}
          </dt>
          <dd className="ml-4">{t("privacyPolicy.faq.q3.answer")}</dd>

          <dt className="mt-3 font-bold">
            {t("privacyPolicy.faq.q4.question")}
          </dt>
          <dd className="ml-4">{t("privacyPolicy.faq.q4.answer")}</dd>

          <dt className="mt-3 font-bold">
            {t("privacyPolicy.faq.q5.question")}
          </dt>
          <dd className="ml-4">
            {t("privacyPolicy.faq.q5.answer", {
              interpolation: { escapeValue: false },
              components: {
                1: (
                  <a
                    href="mailto:privacy@arcology.io"
                    className="text-blue-600 hover:underline"
                  />
                ),
              },
            })}
          </dd>
        </dl>
      </section>

      <section aria-label="Full Privacy Policy" className="w-full">
        <h2 className="mb-4 text-xl font-semibold">
          {t("privacyPolicy.fullPolicy.title")}
        </h2>

        <article className="mb-5">
          <h3 className="mb-2 text-lg font-medium">
            {t("privacyPolicy.whatWeStore.title")}
          </h3>
          <ul className="ml-6 list-disc">
            <li>
              <strong>{t("privacyPolicy.whatWeStore.userSettings")}</strong>
            </li>
            <li>
              <strong>
                {t("privacyPolicy.whatWeStore.documentTemplates")}
              </strong>
            </li>
            <li>
              <strong>{t("privacyPolicy.whatWeStore.uploadedFiles")}</strong>
            </li>
          </ul>
          <p className="mt-2">
            <strong>{t("privacyPolicy.whatWeStore.important")}</strong>
          </p>
        </article>

        <article className="mb-5">
          <h3 className="mb-2 text-lg font-medium">
            {t("privacyPolicy.whyWeCollect.title")}
          </h3>
          <ul className="ml-6 list-disc">
            <li>{t("privacyPolicy.whyWeCollect.personalizedExperience")}</li>
            <li>{t("privacyPolicy.whyWeCollect.processFiles")}</li>
          </ul>
        </article>

        <article className="mb-5">
          <h3 className="mb-2 text-lg font-medium">
            {t("privacyPolicy.legalBasis.title")}
          </h3>
          <p>{t("privacyPolicy.legalBasis.description")}</p>
          <p className="my-2">
            {t("privacyPolicy.legalBasis.consent", {
              components: {
                1: (
                  <a
                    href="mailto:privacy@arcology.io"
                    className="text-blue-600 hover:underline"
                  />
                ),
              },
            })}
          </p>
          <p>{t("privacyPolicy.legalBasis.rightsIntro")}</p>
          <ul className="ml-6 list-disc">
            <li>{t("privacyPolicy.legalBasis.right1")}</li>
            <li>{t("privacyPolicy.legalBasis.right2")}</li>
            <li>{t("privacyPolicy.legalBasis.right3")}</li>
            <li>{t("privacyPolicy.legalBasis.right4")}</li>
            <li>{t("privacyPolicy.legalBasis.right5")}</li>
            <li>{t("privacyPolicy.legalBasis.right6")}</li>
            <li>{t("privacyPolicy.legalBasis.right7")}</li>
          </ul>
          <p className="mt-2">
            <strong>{t("privacyPolicy.legalBasis.noSelling")}</strong>
          </p>
          <p className="mt-2">
            {t("privacyPolicy.legalBasis.exerciseRights", {
              components: {
                1: (
                  <a
                    href="mailto:privacy@arcology.io"
                    className="text-blue-600 hover:underline"
                  />
                ),
              },
            })}
          </p>
          <p className="mt-2">{t("privacyPolicy.legalBasis.noCookies")}</p>
        </article>

        <article className="mb-5">
          <h3 className="mb-2 text-lg font-medium">
            {t("privacyPolicy.aiUsage.title")}
          </h3>
          <p>
            {t("privacyPolicy.aiUsage.groqDescription", {
              components: {
                1: (
                  <a
                    href="https://www.groq.com"
                    className="text-blue-600 hover:underline"
                  />
                ),
              },
            })}
          </p>
          <p className="mt-2">{t("privacyPolicy.aiUsage.noAccess")}</p>
        </article>

        <article className="mb-5">
          <h3 className="mb-2 text-lg font-medium">
            {t("privacyPolicy.dataSecurity.title")}
          </h3>
          <p>{t("privacyPolicy.dataSecurity.description")}</p>
        </article>

        <article className="mb-5">
          <h3 className="mb-2 text-lg font-medium">
            {t("privacyPolicy.dataLocation.title")}
          </h3>
          <p>{t("privacyPolicy.dataLocation.description")}</p>
        </article>

        <article className="mb-5">
          <h3 className="mb-2 text-lg font-medium">
            {t("privacyPolicy.studentData.title")}
          </h3>
          <p>{t("privacyPolicy.studentData.controller")}</p>
          <p className="mt-2">{t("privacyPolicy.studentData.ferpa")}</p>
        </article>

        <article className="mb-5">
          <h3 className="mb-2 text-lg font-medium">
            {t("privacyPolicy.childrenPrivacy.title")}
          </h3>
          <p>{t("privacyPolicy.childrenPrivacy.description")}</p>
        </article>

        <article className="mb-5">
          <h3 className="mb-2 text-lg font-medium">
            {t("privacyPolicy.dpa.title")}
          </h3>
          <p>
            {t("privacyPolicy.dpa.description", {
              components: {
                1: (
                  <a
                    href="mailto:privacy@arcology.io"
                    className="text-blue-600 hover:underline"
                  />
                ),
              },
            })}
          </p>
        </article>

        <article className="mb-5">
          <h3 className="mb-2 text-lg font-medium">
            {t("privacyPolicy.dataRetention.title")}
          </h3>
          <table className="w-full border border-collapse">
            <thead>
              <tr>
                <th className="p-2 text-left border">
                  {t("privacyPolicy.dataRetention.table.dataType")}
                </th>
                <th className="p-2 text-left border">
                  {t("privacyPolicy.dataRetention.table.retentionPeriod")}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="p-2 border">
                  {t("privacyPolicy.dataRetention.table.userConfig")}
                </td>
                <td className="p-2 border">
                  {t("privacyPolicy.dataRetention.table.userConfigRetention")}
                </td>
              </tr>
              <tr>
                <td className="p-2 border">
                  {t("privacyPolicy.dataRetention.table.uploadedFiles")}
                </td>
                <td className="p-2 border">
                  {t(
                    "privacyPolicy.dataRetention.table.uploadedFilesRetention"
                  )}
                </td>
              </tr>
              <tr>
                <td className="p-2 border">
                  {t("privacyPolicy.dataRetention.table.supportEmails")}
                </td>
                <td className="p-2 border">
                  {t(
                    "privacyPolicy.dataRetention.table.supportEmailsRetention"
                  )}
                </td>
              </tr>
            </tbody>
          </table>
        </article>

        <article className="mb-5">
          <h3 className="mb-2 text-lg font-medium">
            {t("privacyPolicy.thirdPartyServices.title")}
          </h3>
          <p>{t("privacyPolicy.thirdPartyServices.infrastructure")}</p>
          <p className="mt-2">{t("privacyPolicy.thirdPartyServices.stytch")}</p>
          <p className="mt-2">{t("privacyPolicy.thirdPartyServices.stripe")}</p>
        </article>

        <article className="mb-5">
          <h3 className="mb-2 text-lg font-medium">
            {t("privacyPolicy.automatedDecisions.title")}
          </h3>
          <p>{t("privacyPolicy.automatedDecisions.description")}</p>
        </article>

        <article className="mb-5">
          <h3 className="mb-2 text-lg font-medium">
            {t("privacyPolicy.dataBreach.title")}
          </h3>
          <p>{t("privacyPolicy.dataBreach.description")}</p>
        </article>

        <article className="mb-5">
          <h3 className="mb-2 text-lg font-medium">
            {t("privacyPolicy.allUsersRights.title")}
          </h3>
          <p>{t("privacyPolicy.allUsersRights.description")}</p>
          <p className="my-2">{t("privacyPolicy.allUsersRights.youCan")}</p>
          <ul className="ml-6 list-disc">
            <li>{t("privacyPolicy.allUsersRights.right1")}</li>
            <li>{t("privacyPolicy.allUsersRights.right2")}</li>
            <li>{t("privacyPolicy.allUsersRights.right3")}</li>
            <li>{t("privacyPolicy.allUsersRights.right4")}</li>
            <li>{t("privacyPolicy.allUsersRights.right5")}</li>
            <li>{t("privacyPolicy.allUsersRights.right6")}</li>
            <li>{t("privacyPolicy.allUsersRights.right7")}</li>
          </ul>
          <p className="mt-2">
            <strong>{t("privacyPolicy.allUsersRights.noSelling")}</strong>
          </p>
          <p className="mt-2">
            {t("privacyPolicy.allUsersRights.exerciseRights", {
              components: {
                1: (
                  <a
                    href="mailto:privacy@arcology.io"
                    className="text-blue-600 hover:underline"
                  />
                ),
              },
            })}
          </p>
        </article>

        <article className="mb-5">
          <h3 className="mb-2 text-lg font-medium">
            {t("privacyPolicy.complaints.title")}
          </h3>
          <p>{t("privacyPolicy.complaints.description")}</p>
          <p className="mt-2">
            {t("privacyPolicy.complaints.euLink", {
              components: {
                1: (
                  <a
                    href="https://edpb.europa.eu/about-edpb/about-edpb/members_en"
                    className="text-blue-600 hover:underline"
                  />
                ),
              },
            })}
          </p>
        </article>

        <article className="mb-5">
          <h3 className="mb-2 text-lg font-medium">
            {t("privacyPolicy.updates.title")}
          </h3>
          <p>{t("privacyPolicy.updates.description")}</p>
        </article>
      </section>

      <footer className="w-full pt-4 mt-8 border-t">
        <h3 className="mb-2 text-lg font-medium">
          {t("privacyPolicy.contact.title")}
        </h3>
        <address className="not-italic">
          <p>{t("privacyPolicy.contact.questions")}</p>
          <p>{t("privacyPolicy.contact.company")}</p>
          <p>
            {t("privacyPolicy.contact.email")}{" "}
            <a
              href="mailto:privacy@arcology.io"
              className="text-blue-600 hover:underline"
            >
              privacy@arcology.io
            </a>
          </p>
          <p>
            {t("privacyPolicy.contact.website")}{" "}
            <a
              href="https://arcology.io"
              className="text-blue-600 hover:underline"
            >
              arcology.io
            </a>
          </p>
        </address>
        <p className="mt-4">{t("privacyPolicy.contact.thanks")}</p>
      </footer>
    </main>
  );
}
